namespace $ {

	export type $easyreels_user_recorder_ticket = Extract<
		ReturnType<typeof $easyreels_user_recorder_start>['startRecording'],
		{ __typename: 'StartRecordingPayload' }
	>['tickets'][number]

	export type $easyreels_user_recorder_chunk = {
		blob: Blob
		segment: number
		start_ms: number
		end_ms: number
		capture: CaptureInput
	}

	type Part = {
		sequence: number
		blob: Blob | null
		bytes: number
		sha256: string
		segment: number
		start_ms: number
		end_ms: number
		capture: CaptureInput
		queued_at: number
		attempts: number
		acked: boolean
		busy: boolean
		ready: boolean
	}

	export class $easyreels_user_recorder_refused extends $easyreels_i18n_error {}

	export type $easyreels_user_recorder_quota = { usedBytes: number, limitBytes: number }

	export const $easyreels_user_recorder_part_limit_bytes = 8 * 1024 * 1024

	const concurrent_uploads = 2
	const upload_timeout_ms = 60_000
	const refill_below = 60
	const refill_before_expiry_ms = 60_000
	const refresh_ms = 1000
	const warn = { count: 6, bytes: 10 * 1024 * 1024, age_ms: 30_000, attempts: 3 }
	const calm = { count: 2, bytes: 4 * 1024 * 1024, age_ms: 15_000 }

	export class $easyreels_user_recorder_uploader extends $mol_object {

		recording = 0
		stream = 0
		parts = new Map<number, Part>()
		tickets = new Map<number, $easyreels_user_recorder_ticket>()
		next_sequence = 0
		active = 0
		refill: Promise<void> | null = null
		limited = false
		halted: Error | null = null
		drains: { resolve: () => void, reject: (error: Error) => void }[] = []
		wakers: (() => void)[] = []
		refresher = 0

		wake = () => this.wakers.splice(0).forEach(wake => wake())

		@$mol_mem
		queue(next?: { count: number, bytes: number }) {
			$mol_wire_solid()
			return next ?? { count: 0, bytes: 0 }
		}

		@$mol_mem
		warning(next?: boolean) {
			$mol_wire_solid()
			return next ?? false
		}

		@$mol_mem
		problem(next?: $easyreels_i18n_note | null) {
			$mol_wire_solid()
			return next ?? null
		}

		fatal(_error: Error) {}

		limit_reached(_quota: $easyreels_user_recorder_quota) {}

		begin(recording: number, tickets: readonly $easyreels_user_recorder_ticket[]) {
			this.recording = recording
			for (const ticket of tickets) this.tickets.set(ticket.sequence, ticket)
			addEventListener('online', this.wake)
			this.refresher = setInterval(() => this.refresh(), refresh_ms) as unknown as number
		}

		add(chunk: $easyreels_user_recorder_chunk) {
			if (this.halted) return
			if (chunk.blob.size > $easyreels_user_recorder_part_limit_bytes) {
				const size = chunk.blob.size
				this.halt(new $easyreels_user_recorder_refused(
					`A part of ${size} bytes is over the limit`,
					$easyreels_i18n_say('$easyreels_user_recorder_part_too_large', () => ({
						size: $easyreels_i18n_bytes(size),
						limit: $easyreels_i18n_bytes($easyreels_user_recorder_part_limit_bytes),
					})),
				))
				return
			}

			const part: Part = {
				...chunk,
				sequence: this.next_sequence++,
				bytes: chunk.blob.size,
				sha256: '',
				queued_at: performance.now(),
				attempts: 0,
				acked: false,
				busy: false,
				ready: false,
			}
			this.parts.set(part.sequence, part)

			this.digest(part).then(
				() => {
					part.ready = true
					this.refresh()
					this.pump()
				},
				error => this.halt(error instanceof Error ? error : new Error(String(error))),
			)
		}

		async digest(part: Part) {
			const digest = await crypto.subtle.digest('SHA-256', await part.blob!.arrayBuffer())
			part.sha256 = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('')
		}

		pump() {
			while (this.active < concurrent_uploads && !this.halted) {
				const part = [...this.parts.values()].find(part => part.ready && !part.acked && !part.busy)
				if (!part) return

				part.busy = true
				this.active++
				this.upload(part).finally(() => {
					part.busy = false
					this.active--
					this.refresh()
					this.pump()
				})
			}
		}

		async upload(part: Part) {
			for (;;) {
				if (this.halted) return

				try {
					const ticket = await this.ticket_for(part.sequence)
					const form = new FormData()
					for (const field of ticket.fields) form.append(field.name, field.value)
					form.append('file', part.blob!)

					const response = await fetch(ticket.url, { method: 'POST', body: form, signal: AbortSignal.timeout(upload_timeout_ms) })
					if (response.ok) break

					const body = await response.text().catch(() => '')
					if (/<Code>Entity(TooLarge|TooSmall)<\/Code>/.test(body)) {
						throw new $easyreels_user_recorder_refused(
							`Part ${part.sequence} was refused by size`,
							$easyreels_i18n_say('$easyreels_user_recorder_part_refused', () => ({ sequence: part.sequence })),
						)
					}
					if (response.status === 403) this.tickets.delete(part.sequence)

					throw new $easyreels_i18n_error(
						`Part ${part.sequence}: HTTP ${response.status}`,
						$easyreels_i18n_say('$easyreels_user_recorder_http_status', () => ({ sequence: part.sequence, status: response.status })),
					)
				} catch (error) {
					if (error instanceof $easyreels_user_recorder_refused) return this.halt(error)

					part.attempts++
					this.problem($easyreels_i18n_error_note(error))
					this.refresh()
					await this.backoff(part.attempts)
				}
			}

			part.acked = true
			part.blob = null
			this.problem(null)
		}

		backoff(attempts: number) {
			const ms = Math.min(30_000, 500 * 2 ** Math.min(attempts, 6)) * (0.8 + Math.random() * 0.4)

			return new Promise<void>(resolve => {
				const timer = setTimeout(resolve, ms)
				this.wakers.push(() => {
					clearTimeout(timer)
					resolve()
				})
			})
		}

		fresh(sequence: number) {
			const ticket = this.tickets.get(sequence)
			return ticket && ticket.expiresAt.unix * 1000 > Date.now() + refill_before_expiry_ms ? ticket : null
		}

		async ticket_for(sequence: number) {
			if (!this.fresh(sequence)) {
				if (this.refill) await this.refill.catch(() => {})
				if (!this.fresh(sequence)) {
					if (this.limited) {
						throw new $easyreels_user_recorder_refused(
							'The storage limit is reached',
							$easyreels_i18n_say('$easyreels_user_recorder_storage_full'),
						)
					}
					await this.request(sequence)
				}
			}

			const highest = Math.max(...this.tickets.keys())
			if (highest - sequence < refill_below && !this.refill && !this.limited) {
				this.request(highest + 1).catch(error => this.problem($easyreels_i18n_error_note(error)))
			}

			const ticket = this.fresh(sequence)
			if (!ticket) {
				throw new $easyreels_i18n_error(
					`No upload permission for part ${sequence}`,
					$easyreels_i18n_say('$easyreels_user_recorder_no_permission', () => ({ sequence })),
				)
			}

			return ticket
		}

		request(from: number) {
			const recordingId = this.recording
			const pending = $mol_wire_async(() => $easyreels_user_recorder_tickets({ input: { recordingId, stream: this.stream, from } }))()
				.then(answer => {
					const payload = answer.requestUploadURLs
					if (payload.__typename === 'ErrorPayload') throw new $easyreels_i18n_error(payload.message, $easyreels_i18n_server(payload.message))
					if (payload.__typename === 'QuotaExceededPayload') {
						this.limited = true
						this.limit_reached(payload)
						return
					}
					for (const ticket of payload.tickets) this.tickets.set(ticket.sequence, ticket)
				})
				.finally(() => {
					if (this.refill === pending) this.refill = null
				})

			this.refill = pending
			return pending
		}

		manifest(): PartInput[] {
			return [...this.parts.values()]
				.sort((a, b) => a.sequence - b.sequence)
				.map(part => ({
					sequence: part.sequence,
					bytes: part.bytes,
					sha256: part.sha256,
					segment: part.segment,
					startMs: part.start_ms,
					endMs: part.end_ms,
					capture: part.capture,
				}))
		}

		drain() {
			this.refresh()
			if (this.halted) return Promise.reject(this.halted)
			if ([...this.parts.values()].every(part => part.acked)) return Promise.resolve()

			return new Promise<void>((resolve, reject) => this.drains.push({ resolve, reject }))
		}

		refresh() {
			let count = 0
			let bytes = 0
			let age = 0
			let retrying = false
			const now = performance.now()

			for (const part of this.parts.values()) {
				if (part.acked) continue
				count++
				bytes += part.bytes
				age = Math.max(age, now - part.queued_at)
				retrying ||= part.attempts >= warn.attempts
			}

			this.queue({ count, bytes })

			if (count >= warn.count || bytes >= warn.bytes || age >= warn.age_ms || retrying) this.warning(true)
			else if (count <= calm.count && bytes < calm.bytes && age < calm.age_ms) this.warning(false)

			if (count === 0 && this.active === 0) this.drains.splice(0).forEach(drain => drain.resolve())
		}

		halt(error: Error) {
			if (this.halted) return
			this.halted = error
			this.stop()
			this.drains.splice(0).forEach(drain => drain.reject(error))
			this.fatal(error)
		}

		stop() {
			clearInterval(this.refresher)
			removeEventListener('online', this.wake)
			this.wake()
		}

		override destructor() {
			this.halted ??= new Error('The uploader was closed')
			this.stop()
		}

	}

}
